// Album hooks — TanStack Query wrappers around the album API client (W3 §5).
//
// Exports `useAlbumList`, `useAlbumDetail`, `useAlbumItems` queries +
// `useCreateAlbum`, `useUploadMedia`, `useDeleteAlbum`, `useDeleteAlbumItem`
// mutations.  Mutations invalidate the appropriate query keys via the
// `queryKeys.album.*` factory.

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseQueryOptions,
} from "@tanstack/react-query";

import {
  type AlbumCreateInput,
  type AlbumItemListResponse,
  type AlbumItemRead,
  type AlbumItemSort,
  type AlbumRead,
  type AlbumUpdateInput,
  createAlbum,
  deleteAlbum,
  deleteAlbumItem,
  listAlbumItems,
  listAlbums,
  getAlbum,
  updateAlbum,
  uploadMedia,
  type UploadMediaOptions,
} from "@/lib/api/album";
import { queryKeys } from "@/lib/queries/keys";

// ----- Queries ------------------------------------------------------------

export function useAlbumList(options?: Partial<UseQueryOptions<AlbumRead[], Error>>) {
  return useQuery<AlbumRead[], Error>({
    queryKey: queryKeys.album.list(),
    queryFn: listAlbums,
    staleTime: 30_000,
    ...options,
  });
}

export function useAlbumDetail(
  albumId: string | undefined,
  options?: Partial<UseQueryOptions<AlbumRead, Error>>,
) {
  return useQuery<AlbumRead, Error>({
    queryKey: queryKeys.album.detail(albumId ?? ""),
    queryFn: () => getAlbum(albumId ?? ""),
    enabled: Boolean(albumId),
    ...options,
  });
}

export function useAlbumItems(
  albumId: string | undefined,
  sort: AlbumItemSort = "taken_at_desc",
  options?: Partial<UseQueryOptions<AlbumItemListResponse, Error>>,
) {
  return useQuery<AlbumItemListResponse, Error>({
    queryKey: queryKeys.album.items(albumId ?? "", sort),
    queryFn: () => listAlbumItems(albumId ?? "", { sort }),
    enabled: Boolean(albumId),
    ...options,
  });
}

/**
 * Every item in the album, fetched a page at a time.
 *
 * The single-page `useAlbumItems` above is capped by the endpoint's own default
 * `limit=50`, and nothing in the album UI paged past it: a 58-photo album
 * rendered 50 tiles, showed no "load more" anywhere, and the detail hero said
 * 「50 張相片」 while the list card for the same album said 58. The response has
 * carried `total` the whole time; nothing read it.
 *
 * `getNextPageParam` counts what has actually been loaded across pages rather
 * than assuming each page is exactly `pageSize` long, so a short page near the
 * end cannot make this loop forever or stop one page early.
 */
export function useAlbumItemsPaged(
  albumId: string | undefined,
  sort: AlbumItemSort = "taken_at_desc",
  pageSize = 50,
) {
  return useInfiniteQuery<AlbumItemListResponse, Error, InfiniteData<AlbumItemListResponse>, readonly unknown[], number>({
    queryKey: [...queryKeys.album.items(albumId ?? "", sort), "paged", pageSize],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      listAlbumItems(albumId ?? "", { sort, limit: pageSize, offset: pageParam }),
    getNextPageParam: (last, allPages) => {
      const total = last.total ?? 0;
      const loaded = allPages.reduce((n, p) => n + (p.items?.length ?? 0), 0);
      return loaded < total ? loaded : undefined;
    },
    enabled: Boolean(albumId),
  });
}

// ----- Mutations -----------------------------------------------------------

export function useCreateAlbum() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AlbumCreateInput) => createAlbum(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.album.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useDeleteAlbum() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (albumId: string) => deleteAlbum(albumId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.album.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

/**
 * Rename / re-describe an album. `updateAlbum` and the `PATCH /albums/{id}`
 * route have both existed since W3; this hook is what the list screen's
 * 「重新命名」 row was missing, along with a `useDeleteAlbum` call.
 */
export function useUpdateAlbum() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ albumId, ...payload }: { albumId: string } & AlbumUpdateInput) =>
      updateAlbum(albumId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.album.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useDeleteAlbumItem(albumId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => deleteAlbumItem(albumId, itemId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.album.detail(albumId) });
      void qc.invalidateQueries({ queryKey: queryKeys.album.list() });
      void qc.invalidateQueries({
        queryKey: queryKeys.album.items(albumId, "taken_at_desc"),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useUploadMedia(albumId?: string) {
  const qc = useQueryClient();
  return useMutation<AlbumItemRead[], Error, { files: File[]; options?: UploadMediaOptions }>({
    mutationFn: ({ files, options }) => {
      // Prefer the hook's albumId unless overridden by `options.albumId`.
      const merged: UploadMediaOptions = {
        ...options,
        albumId: options?.albumId ?? albumId ?? null,
      };
      return uploadMedia(files, merged);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.album.all });
      // Also refresh the media module's keys (legacy PhotoUploader etc.).
      void qc.invalidateQueries({ queryKey: queryKeys.media.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}
